// Region display helpers for the public Match Nights page.
// scheduledAt always arrives from the API as a UTC ISO string; labels use
// the night's region timezone (CET for EU, ET for NA).

const REGION_TIME_ZONES = {
  EU: { timeZone: "Europe/Berlin", abbreviation: "CET" },
  NA: { timeZone: "America/New_York", abbreviation: "ET" },
};

function getRegionTimeZone(region) {
  return REGION_TIME_ZONES[region] || REGION_TIME_ZONES.EU;
}

function formatNightLabel(date, region, { uppercaseWeekday = false } = {}) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const { timeZone, abbreviation } = getRegionTimeZone(region);
  const dayFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "long",
  });
  const timeFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const weekday = uppercaseWeekday
    ? dayFormatter.format(date).toUpperCase()
    : dayFormatter.format(date);
  return `${weekday}, ${timeFormatter.format(date)} ${abbreviation}`;
}

function calendarDayKey(date, timeZone) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

// True when the night's local calendar day matches `now` in that region's timezone.
export function isNightScheduledToday(date, region, now = new Date()) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  const current = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(current.getTime())) return false;
  const { timeZone } = getRegionTimeZone(region);
  return calendarDayKey(date, timeZone) === calendarDayKey(current, timeZone);
}

// Card date label, e.g. "THURSDAY, 20:00 CET" or "THURSDAY, 20:00 ET".
export function formatNightDateLabel(date, region) {
  return formatNightLabel(date, region, { uppercaseWeekday: true });
}

// Confirmation copy label, e.g. "Thursday, 20:00 CET".
export function formatNightSentenceLabel(date, region) {
  return formatNightLabel(date, region, { uppercaseWeekday: false });
}

const SERVER_LOCATION_DISPLAY_NAMES = {
  frankfurt: "Frankfurt",
  london: "London",
  ohio: "Ohio",
};

// Servers line, e.g. "EU. Frankfurt and London." / "NA. Ohio."
export function formatServersLine(region, serverLocations) {
  const names = (serverLocations || []).map(
    (loc) => SERVER_LOCATION_DISPLAY_NAMES[loc] || loc
  );
  if (names.length === 0) return `${region}.`;
  if (names.length === 1) return `${region}. ${names[0]}.`;
  const last = names[names.length - 1];
  const rest = names.slice(0, -1);
  return `${region}. ${rest.join(", ")} and ${last}.`;
}
